import React, { useEffect } from 'react';
import { TouchableOpacity, View } from 'react-native';
import {
  Button,
  DefaultScreenContainer,
  InsuranceCardBenefit,
  OfferBanner,
  Text,
} from '~/components';
import useAppNavigation from '~/hooks/useAppNavigation';
import { isWeb } from '~/utils/device';

import ProtectedSVG from '~/assets/svgs/protection-shield.svg';
import LucklyCloverSVG from '~/assets/svgs/luckly-clover.svg';

import BannerImg from '~/assets/img/banners/unemployment-assistance-banner.png';
import BannerImgDesk from '~/assets/img/banners/unemployment-assistance-desk.png';
import type { Benefit } from '~/components/InsuranceCardBenefit';
import { journeyLogUseCase } from '~/useCases/PSF/JourneyLogUseCase';
import { useUserInfoStore } from '~/stores/userInfo';
import GeneralInfoFooter from '../components/GeneralInfoFooter';
import { useSalesFunnel } from '~/stores';
import { MarisaAppUtils } from '~/utils/MarisaAppUtils';

const INSURANCE_TYPE = 'unemployment-assistance';

const BENEFITS: Benefit[] = [
  {
    icon: ProtectedSVG,
    title: 'Em caso de desemprego involuntário',
    description: (
      <Text className="text-sm mt-0">
        {`● Direito a cartão alimentação de R$ 200,00 por até 3 meses;\n\n● Avaliação e/ou elaboração do currículo para recolocação (1 única vez por e-mail)\n\n● Seu currículo disponibilizado em site de empregos por até 3 meses (sem custos adicionais)\n\n● Elegível para CLT e autônomos/ liberais mediante a comprovação de renda`}
      </Text>
    ),
  },
  {
    icon: LucklyCloverSVG,
    title: 'Concorra a prêmios',
    description: (
      <Text className="text-sm">
        Concorra a sorteios semanais de{' '}
        <Text className="text-primary-600 font-medium">R$ 1.500,00</Text>{' '}
        líquidos de IR pela loteria federal.
      </Text>
    ),
  },
];

const UnemploymentAssistanceDetails: React.FC = () => {
  const { goBack } = useAppNavigation();
  const { init } = useSalesFunnel();

  const id = useUserInfoStore((state) => state.user?.id);

  useEffect(() => {
    if (id) {
      journeyLogUseCase(useUserInfoStore.getState().user, 'ASSIST_DESEMPREGO');
    }
  }, [id, init]);

  return (
    <DefaultScreenContainer>
      <OfferBanner
        hireInsurance={{
          showButton: true,
          type: INSURANCE_TYPE,
          label: 'Contrate em nossas lojas',
          onPressButton: MarisaAppUtils.goToStoresList,
        }}
        images={{
          mobile: BannerImg,
          web: { banner: BannerImgDesk },
        }}
        insuranceName="Assistência Desemprego"
        insuranceDetail="Segurança e tranquilidade para você"
      />

      <View className="px-6">
        <View className="md:items-center md:justify-center items-start justify-start">
          <Text className="mt-6 sm:mt-16 text-gray-700 text-[22px]">
            O que é o Assistência desemprego?
          </Text>

          <Text className="mt-4 text-gray-400 md:text-center text-[16px] leading-6">
            Amparo e Proteção para você em caso de desemprego involuntário.
          </Text>
        </View>

        {isWeb && (
          <View className="mt-10 mb-2 bg-gray-100 h-[1px] invisible md:visible" />
        )}

        <View className="bg-protection-200 p-3 rounded-lg mt-8">
          <View className="border border-primary-600 rounded-lg py-6 px-3 bg-light-100 shadow-sm">
            <Text className="text-center text-primary-600 font-medium">
              E ainda concorra a sorteios semanais
            </Text>
          </View>
        </View>

        <Text className="mt-3 text-xl text-gray-700 md:items-center md:justify-center md:text-center md:mb-6">
          Conheça os benefícios:
        </Text>

        <View className="lg:flex-row lg:justify-center">
          {BENEFITS.map((benefit, index) => (
            <InsuranceCardBenefit
              data={benefit}
              type="protection"
              key={index}
            />
          ))}
        </View>

        <View className="items-center justify-center py-8 bg-feedback-neutral-bkg rounded-lg mt-8 mb-7">
          <Text className="text-secondary-800 text-sm">
            Tudo isso por R$ <Text className="text-xl font-medium">5,99</Text>
            /mês
          </Text>
        </View>

        <Text className="text-gray-400 text-[12px] text-center my-4">
          Forma de pagamento: Pix, Dinheiro, Débito, Cartão Marisa ou no Cartão
          de Crédito de sua preferência.
        </Text>

        <Button onPress={MarisaAppUtils.goToStoresList}>
          Contrate em nossas lojas
        </Button>

        <GeneralInfoFooter className="mt-12" />
      </View>

      <View className="py-4 px-6 items-center justify-center lg:mx-auto lg:w-[328px]">
        {isWeb && (
          <TouchableOpacity onPress={() => goBack()}>
            <Text className="text-primary-600 uppercase font-medium mt-4 text-xs">
              Voltar
            </Text>
          </TouchableOpacity>
        )}
      </View>
    </DefaultScreenContainer>
  );
};

export default UnemploymentAssistanceDetails;
